import type {
  RecommendedChip,
  TransferRecommendation
} from '@api-service/recommendations';

type PlayerFixture = TransferRecommendation['in']['fixtures'][number];

export const sumPredictedPoints = (fixtures: PlayerFixture[]) =>
  fixtures.reduce(
    (total, fixture) => total + (fixture.predictedPoints ?? 0),
    0
  );

// The gain must cover the same fixtures as the per-player totals and the
// "Predicted GW1–6" column, so the three figures on a row reconcile (FFH-2154).
export const predictedPointsDelta = (transfer: TransferRecommendation) =>
  sumPredictedPoints(transfer.in.fixtures ?? []) -
  sumPredictedPoints(transfer.out.fixtures ?? []);

export const priceDelta = (transfer: TransferRecommendation) =>
  transfer.in.price - transfer.out.price;

export const totalPredictedPointsGain = (transfers: TransferRecommendation[]) =>
  transfers.reduce(
    (total, transfer) => total + predictedPointsDelta(transfer),
    0
  );

/** The footer renders the total to one decimal place. */
const GAIN_DISPLAY_DECIMAL_PLACES = 1;

/**
 * The chips whose recommendation is a squad rebuild rather than a set of
 * discretionary transfers. Both hand the solver unlimited transfers, so it
 * replaces most of the squad at once and the in/out rows are an arbitrary
 * position-matched pairing of that rebuild rather than swaps anyone chose.
 *
 * Bench boost and triple captain are deliberately absent: they are played
 * alongside ordinary transfers, so a negative total there still reads as the
 * mistake the guardrail exists to catch.
 */
const SQUAD_REBUILD_CHIPS: ReadonlySet<RecommendedChip> =
  new Set<RecommendedChip>(['WILDCARD', 'FREE_HIT']);

/**
 * Withholds a recommendation set whose own headline total is negative, so the
 * recommended view never presents transfers it simultaneously reports as
 * losing points (FFH-2518). The solver can legitimately open an
 * objective-positive multi-week plan with a locally negative first week, but
 * until the API can say why, the set reads as a mistake and is suppressed in
 * favour of the "no transfers recommended" empty state.
 *
 * A wildcard or free hit the member has actually activated is exempt. There the
 * total is not a verdict on a choice they made: the whole squad has been
 * rebuilt under the chip, the rows are an arbitrary pairing of that rebuild,
 * and the sum is taken over the full displayed span rather than the shorter
 * horizon the solver optimised, so the two can disagree by construction.
 * Suppressing it leaves a member who has just spent a chip with an empty card
 * and no sight of the squad it bought them.
 *
 * The exemption needs both halves. `recommendedChip` reports what the solver
 * played, which is not the same as what the planner holds: a chip strategy
 * window can have it elect a rebuild the member never activated. Committing
 * those rows makes ordinary permanent transfers, since the save path writes
 * transfer events and never activates a chip, so a squad's worth of them would
 * be paid for a hit at a time. `hasUnlimitedTransfers` comes from the planner's
 * own state for the gameweek, and is what makes the rows free to take.
 *
 * Recommended view only. The custom flow answers an explicit request, where
 * withholding the answer would be worse than a negative number.
 *
 * The total must be the same figure the footer prints: both come from
 * {@link totalPredictedPointsGain}, and suppression triggers only when the
 * total is negative at the footer's own precision. A raw `< 0` comparison
 * would let IEEE-754 noise (decimals that cancel to -5.5e-17 instead of 0)
 * withhold a set whose displayed total is zero.
 */
export const applyNegativeTotalGuardrail = (
  transfers: TransferRecommendation[],
  recommendedChip: RecommendedChip = null,
  hasUnlimitedTransfers = false
): TransferRecommendation[] => {
  if (hasUnlimitedTransfers && SQUAD_REBUILD_CHIPS.has(recommendedChip)) {
    return transfers;
  }

  const totalGain = totalPredictedPointsGain(transfers);
  const displayedMagnitude = Number(
    Math.abs(totalGain).toFixed(GAIN_DISPLAY_DECIMAL_PLACES)
  );
  const isDisplayedNegative = totalGain < 0 && displayedMagnitude > 0;
  return isDisplayedNegative ? [] : transfers;
};
